<template>
  <v-container fluid class="home-container pa-0">

    <!-- ── Hero bar ─────────────────────────────────────────────── -->
    <div class="hero-bar px-6 pt-6 pb-4">
      <div class="hero-text">
        <h1 class="hero-title">Best deals <span class="accent--text">right now</span></h1>
        <p class="hero-sub">Live prices from {{ activeStores.length }} stores — refreshed daily</p>
      </div>
      <div class="stat-pills">
        <div class="stat-pill">
          <span class="stat-val secondary--text">{{ totalDeals.toLocaleString() }}</span>
          <span class="stat-lbl">active deals</span>
        </div>
        <div class="stat-pill">
          <span class="stat-val secondary--text">{{ activeStores.length }}</span>
          <span class="stat-lbl">stores live</span>
        </div>
      </div>
    </div>

    <!-- ── Filter chips ──────────────────────────────────────────── -->
    <div class="filter-row px-6 pb-4">
      <v-chip
        v-for="chip in filterChips"
        :key="chip.key"
        :color="activeFilter === chip.key ? 'primary' : ''"
        :outlined="activeFilter !== chip.key"
        small
        class="mr-2 mb-1 filter-chip"
        @click="activeFilter = chip.key"
      >{{ chip.label }}</v-chip>

      <v-spacer />

      <v-select
        v-model="sortBy"
        :items="sortOptions"
        dense
        outlined
        hide-details
        class="sort-select"
        style="max-width:190px"
        prepend-inner-icon="mdi-sort"
      />
    </div>

    <v-divider class="mx-6" />

    <!-- ── Store grid ─────────────────────────────────────────────── -->
    <v-row no-gutters class="store-grid ma-0 px-4 pt-4">
      <v-col
        v-for="store in visibleStores"
        :key="store.meta.key"
        cols="12" sm="6" md="4" lg="3"
        class="pa-2"
      >
        <v-card class="store-card" flat outlined>

          <!-- card header -->
          <v-card-title class="card-header py-3 px-4">
            <v-avatar
              size="38"
              rounded
              class="mr-3 store-avatar"
              :color="store.meta.color + '22'"
            >
              <v-img
                v-if="storeLogoUrl(store.meta.id)"
                :src="storeLogoUrl(store.meta.id)"
                contain
              />
              <span
                v-else
                class="store-abbr font-weight-bold"
                :style="{ color: store.meta.color }"
              >{{ store.meta.abbr }}</span>
            </v-avatar>
            <div>
              <div class="store-name-text">{{ store.meta.name }}</div>
              <div class="store-subtitle">top 5 deals today</div>
            </div>
          </v-card-title>

          <v-divider />

          <!-- skeleton while loading -->
          <template v-if="loading">
            <v-list dense class="pa-0">
              <v-list-item v-for="n in 5" :key="n">
                <v-skeleton-loader type="list-item-two-line" class="full-width" />
              </v-list-item>
            </v-list>
          </template>

          <!-- deal rows -->
          <template v-else>
            <v-list dense class="deal-list pa-0">
              <v-list-item
                v-for="(deal, i) in store.deals"
                :key="i"
                class="deal-row px-4"
              >
                <v-list-item-avatar
                  tile size="34" class="my-1 mr-3 deal-thumb-wrap"
                  style="cursor:pointer"
                  @click="$router.push(`/game/${encodeURIComponent(deal.dealID)}`)"
                >
                  <v-img
                    :src="deal.thumb"
                    :lazy-src="deal.thumb"
                    contain
                    class="deal-thumb"
                  >
                    <template #placeholder>
                      <v-sheet
                        class="fill-height d-flex align-center justify-center"
                        :color="store.meta.color + '33'"
                        tile
                      >
                        <span class="caption font-weight-bold" :style="{ color: store.meta.color }">
                          {{ deal.title ? deal.title[0] : '?' }}
                        </span>
                      </v-sheet>
                    </template>
                  </v-img>
                </v-list-item-avatar>

                <v-list-item-content
                  style="cursor:pointer"
                  @click="$router.push(`/game/${encodeURIComponent(deal.dealID)}`)"
                >
                  <v-list-item-title class="deal-title">{{ deal.title }}</v-list-item-title>
                  <v-list-item-subtitle class="deal-prices">
                    <span class="orig-price">${{ deal.normalPrice }}</span>
                    <span class="sale-price accent--text ml-2">${{ deal.salePrice }}</span>
                    <v-chip
                      x-small
                      class="ml-2 save-chip"
                      color="success"
                      text-color="white"
                    >-{{ savingsPct(deal) }}%</v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action class="my-0">
                  <v-btn icon x-small @click.stop="toggleWishlist(deal)" :title="isWishlisted(deal) ? 'Remove from wishlist' : 'Add to wishlist'">
                    <v-icon x-small :color="isWishlisted(deal) ? 'accent' : '#7b7a8e'">
                      {{ isWishlisted(deal) ? 'mdi-heart' : 'mdi-heart-outline' }}
                    </v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>

              <v-list-item v-if="!store.deals || store.deals.length === 0">
                <v-list-item-content>
                  <v-list-item-subtitle class="text-center py-2">No deals right now</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </template>

          <v-divider />

          <!-- card footer -->
          <v-card-actions class="px-4 py-2">
            <v-btn
              text
              x-small
              color="primary"
              :to="`/browser?storeID=${store.meta.id}`"
            >
              View all
              <v-icon right x-small>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card-actions>

        </v-card>
      </v-col>
    </v-row>

  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import { STORES_META } from '@/store/index'
import { loadDeals, rebuildListDeals, ensureStoresLoaded } from '@/utils/deals'

export default {
  name: 'HomeView',

  data () {
    return {
      loading:      true,
      activeFilter: 'all',
      sortBy:       'savings',

      filterChips: [
        { key: 'all',      label: 'All stores' },
        { key: 'steam',    label: 'Steam' },
        { key: 'epicGameStore', label: 'Epic' },
        { key: 'gog',      label: 'GOG' },
        { key: 'humblebundle', label: 'Humble' },
        { key: 'fanatical',label: 'Fanatical' },
      ],

      sortOptions: [
        { text: 'Top savings',   value: 'savings'  },
        { text: 'Lowest price',  value: 'price'    },
        { text: 'Store name',    value: 'name'      },
      ],
    }
  },

  computed: {
    ...mapState(['listStores', 'listDeals', 'wishlist']),

    // All stores that are active (have isActive flag from API)
    activeStores () {
      const activeIds = new Set(
        this.listStores.filter(s => s.isActive).map(s => Number(s.storeID))
      )
      return STORES_META.filter(m => activeIds.has(m.id) || this.listStores.length === 0)
    },

    totalDeals () {
      return this.listDeals ? this.listDeals.length : 0
    },

    // Merge STORES_META with live deal data from Vuex
    storeData () {
      return STORES_META.map(meta => ({
        meta,
        deals: this.topDeals(meta.key),
      }))
    },

    storeLogoMap () {
      const map = {}
      for (const s of this.listStores) {
        if (s.images && s.images.logo) map[Number(s.storeID)] = `https://www.cheapshark.com/${s.images.logo}`
      }
      return map
    },

    // Apply filter + sort
    visibleStores () {
      let list = this.storeData

      if (this.activeFilter !== 'all') {
        list = list.filter(s => s.meta.key === this.activeFilter)
      }

      if (this.sortBy === 'name') {
        list = [...list].sort((a, b) => a.meta.name.localeCompare(b.meta.name))
      } else if (this.sortBy === 'savings') {
        list = [...list].sort((a, b) => {
          const avgA = this.avgSavings(a.deals)
          const avgB = this.avgSavings(b.deals)
          return avgB - avgA
        })
      } else if (this.sortBy === 'price') {
        list = [...list].sort((a, b) => {
          const minA = this.minPrice(a.deals)
          const minB = this.minPrice(b.deals)
          return minA - minB
        })
      }

      return list
    },
  },

  created () {
    this.getAllDeals()
    this.getAllStores()
  },

  methods: {
    // Return top-5 deals for a given store key from Vuex state
    topDeals (key) {
      return (this.$store.state[key] || []).slice(0, 5)
    },

    savingsPct (deal) {
      if (deal.savings) return Math.round(parseFloat(deal.savings))
      const orig = parseFloat(deal.normalPrice)
      const sale = parseFloat(deal.salePrice)
      if (!orig || orig === 0) return 0
      return Math.round(((orig - sale) / orig) * 100)
    },

    avgSavings (deals) {
      if (!deals || deals.length === 0) return 0
      return deals.reduce((sum, d) => sum + this.savingsPct(d), 0) / deals.length
    },

    minPrice (deals) {
      if (!deals || deals.length === 0) return Infinity
      return Math.min(...deals.map(d => parseFloat(d.salePrice) || Infinity))
    },

    storeLogoUrl (storeId) {
      return this.storeLogoMap[storeId] || null
    },

    async getAllDeals () {
      const today      = new Date().toDateString()
      const cachedDate = localStorage.getItem('date')

      // vuex-persistedstate restores the per-store arrays (listDeals is not
      // persisted) — those are the signal that today's data is already local.
      const restored = STORES_META.some(s => (this.$store.state[s.key] || []).length > 0)
      if (restored && cachedDate === today) {
        if (!this.listDeals || this.listDeals.length === 0) rebuildListDeals(this.$store)
        this.loading = false
        return
      }

      try {
        await loadDeals(this.$store)
        localStorage.setItem('date', today)
      } catch (err) {
        console.error('Failed to fetch deals:', err)
      } finally {
        this.loading = false
      }
    },

    async getAllStores () {
      try {
        await ensureStoresLoaded(this.$store)
      } catch (err) {
        console.error('Failed to fetch stores:', err)
      }
    },

    isWishlisted (deal) {
      return this.wishlist && this.wishlist.some(d => d.gameID === deal.gameID)
    },

    toggleWishlist (deal) {
      if (this.isWishlisted(deal)) {
        this.$store.commit('removeFromWishlist', deal.gameID)
      } else {
        this.$store.commit('addToWishlist', deal)
      }
    },
  },
}
</script>

<style scoped>
.home-container {
  background: #0e0e10;
  min-height: 100vh;
}

/* ── Hero ─────────────────────────────────── */
.hero-bar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.hero-title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.5px;
  line-height: 1.15;
  color: #f0eff6;
}

.hero-sub {
  font-size: 13px;
  color: #7b7a8e;
  margin-top: 4px;
}

.stat-pills {
  display: flex;
  gap: 10px;
}

.stat-pill {
  background: #18181c;
  border: 1px solid #2e2e38;
  border-radius: 10px;
  padding: 10px 16px;
  text-align: center;
  display: flex;
  flex-direction: column;
}

.stat-val {
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
}

.stat-lbl {
  font-size: 11px;
  color: #7b7a8e;
  margin-top: 3px;
}

/* ── Filters ──────────────────────────────── */
.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding-top: 16px;
}

.filter-chip {
  font-size: 12px !important;
  letter-spacing: 0 !important;
}

/* ── Cards ────────────────────────────────── */
.store-card {
  background: #1f1f24 !important;
  border-color: #2e2e38 !important;
  border-radius: 12px !important;
  transition: border-color 0.15s, background 0.15s;
}

.store-card:hover {
  border-color: #7c6ff7 !important;
  background: #232329 !important;
}

.card-header {
  background: #18181c;
  border-radius: 12px 12px 0 0 !important;
}

.store-avatar {
  border-radius: 8px !important;
}

.store-abbr {
  font-size: 13px;
}

.store-name-text {
  font-size: 13px;
  font-weight: 700;
  color: #f0eff6;
  line-height: 1.2;
}

.store-subtitle {
  font-size: 10px;
  color: #7b7a8e;
  margin-top: 1px;
}

/* ── Deal rows ────────────────────────────── */
.deal-list {
  background: transparent !important;
}

.deal-row {
  min-height: 48px !important;
  transition: background 0.1s;
}

.deal-row:hover {
  background: rgba(124, 111, 247, 0.08) !important;
}

.deal-thumb-wrap {
  border-radius: 4px !important;
  overflow: hidden;
}

.deal-thumb {
  border-radius: 4px;
}

.deal-title {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: #f0eff6 !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.deal-prices {
  display: flex;
  align-items: center;
  font-size: 11px !important;
}

.orig-price {
  text-decoration: line-through;
  color: #7b7a8e;
  font-family: 'Roboto Mono', monospace;
  font-size: 11px;
}

.sale-price {
  font-family: 'Roboto Mono', monospace;
  font-size: 12px;
  font-weight: 500;
}

.save-chip {
  font-size: 10px !important;
  height: 16px !important;
  font-family: 'Roboto Mono', monospace;
}
</style>
